/* eslint-disable quotes */
import React, {useEffect, useState, Fragment} from 'react';
import {useDispatch} from 'react-redux';
import {gql} from 'apollo-boost';
import {useQuery, useMutation} from '@apollo/react-hooks';
import {useParams, useHistory} from 'react-router-dom';
import {FontAwesomeIcon, Breadcrumbs, Loading} from 'worksource-ui';
import {Button, Card, NonIdealState} from '@blueprintjs/core';
import HeaderBar from 'components/header-bar';
import classNames from 'classnames';
import mixpanel from 'mixpanel-browser';

import QASearchBar from 'components/QASearchBar';
import AnalyticsActions from 'services/analytics/actions';
import {EventIdentifier} from 'services/analytics/constants';
import {showGQLError} from 'utils/toaster';
import withMainLayout from 'layouts/main/view';
import ShareFilesBar from 'components/ShareFilesBar';
import FilePreview from 'components/FilePreview';

const FETCH_PAGE = gql`
  query PagesShowPageFetch($clientId: ID!, $guideId: ID!, $pageId: ID!) {
    viewerState @client {
      selectingFiles,
      selectedFiles {
        id,
        name,
        url,
        thumbnailUrl,
        confidential,
      },
    },
    clientFeatures(clientId: $clientId) {
      ragEnabled
    },
    guides(clientId: $clientId, id: $guideId) {
      name,
      page(id: $pageId) {
        id,
        name,
        icon,
        note,
        files {
          id,
          name,
          url,
          thumbnailUrl,
          confidential,
        },
        childPages {
          id,
          name,
          icon,
          pageType,
          note,
          files {
            id,
            name,
            url,
            thumbnailUrl,
            confidential,
          },
        },
        parent {
          id,
          name,
        },
      },
    }
  }
`;

const TOGGLE_FILE_SELECTION = gql`
  mutation($file: SelectedFile) {
    toggleFileSelection(file: $file) @client
  }
`;

const SET_SELECTING_FILES = gql`
  mutation($value: Boolean!) {
    setSelectingFiles(value: $value) @client
  }
`;

const PagesShowPage = () => {
  ////
  // Hooks
  //
  const dispatch = useDispatch();
  const history = useHistory();
  const {clientId, guideId, pageId} = useParams();
  const [openSectionId, setOpenSectionId] = useState(null);

  useEffect(() => {
    mixpanel.track('Open page viewer', {clientId, guideId, pageId});
    dispatch(AnalyticsActions.trigger({
      clientId,
      guideId,
      identifier: EventIdentifier.PAGE_OPEN,
      additionals: {page_id: pageId},
    }));
  }, []);

  ////
  // GraphQL
  //
  const {loading: loadingFetch, data} = useQuery(FETCH_PAGE, {
    variables: {clientId, guideId, pageId},
    onError: error => showGQLError({error}),
  });
  const [toggleFileSelection] = useMutation(TOGGLE_FILE_SELECTION);
  const [setSelectingFiles] = useMutation(SET_SELECTING_FILES);

  ////
  // Events
  //
  const onFocusSearch = () => {
    history.push(`/clients/${clientId}/guides/${guideId}/search`);
  };

  const openFile = ({file}) => {
    if (data.viewerState.selectingFiles && file.confidential) {
      alert("Your administrator has marked this file as confidential. It cannot be shared nor downloaded.");
      return;
    }
    if (data.viewerState.selectingFiles) {
      toggleFileSelection({variables: {file}});
      return;
    }

    history.push(`/clients/${clientId}/guides/${guideId}/pages/${data.guides[0].page.id}/files/${file.id}`);
  };

  const openChildPage = ({page}) => {
    history.push(`/clients/${clientId}/guides/${guideId}/pages/${page.id}`);
  };

  ////
  // Render
  //
  if (loadingFetch || !data) {
    return (
      <Loading />
    );
  }

  const {
    guides: [guide],
    viewerState: {selectingFiles, ...viewerState},
    clientFeatures,
  } = data;
  const {page} = guide;
  const selectedFileIds = new Set(viewerState.selectedFiles.map(file => file.id));

  const sections = page.childPages.filter(p => p.pageType === 'section');
  const childPages = page.childPages.filter(p => p.pageType === 'page');

  return (
    <Fragment>
      <HeaderBar>
        <Breadcrumbs>
          <Breadcrumbs.Breadcrumb
            href={`/clients/${clientId}/guides`}
            icon="book"
            text="Guides"
          />
          <Breadcrumbs.Breadcrumb
            href={`/clients/${clientId}/guides/${guideId}`}
            text={guide.name}
          />
          {page.parent && (
            <Breadcrumbs.Breadcrumb
              text={page.parent.name}
              href={`/clients/${clientId}/guides/${guideId}/pages/${page.parent.id}`}
            />
          )}
          <Breadcrumbs.Breadcrumb
            current
            href={`/clients/${clientId}/guides/${guideId}/${page.id}`}
            text={page.name}
          />
        </Breadcrumbs>
        <div className="spacer" />
        {!selectingFiles && (
          <Button
            icon="multi-select"
            text="Select files"
            onClick={() => setSelectingFiles({variables: {value: true}})}
          />
        )}
      </HeaderBar>

      <ShareFilesBar clientId={clientId} guideId={guideId} />

      <div className="content-container">
        <div className="layout--full">
          {clientFeatures?.ragEnabled && (
            <QASearchBar showHeader onFocus={onFocusSearch} />
          )}

          {page.note && (
            <Fragment>
              <div className="pages-show--header">Notes</div>
              <Card className="pages-show--note">
                <p>{page.note}</p>
              </Card>
            </Fragment>
          )}
          {childPages.length > 0 && (
            <div className="pages-show--header">Child pages</div>
          )}
          {childPages.map((childPage) => {
            return (
              <div
                key={childPage.id}
                className="pages-show--child-page"
                onClick={() => openChildPage({page: childPage})}
              >
                <FontAwesomeIcon name={childPage.icon} />
                <div className="pages-show--child-page-name">{childPage.name}</div>
                <FontAwesomeIcon name="link" />
              </div>
            );
          })}

          {sections.length > 0 && (
            <div className="pages-show--header">Sections</div>
          )}
          {sections.length > 0 && (
            <div className="pages-show--sections">
              {sections.map((section) => {
                const isOpen = openSectionId === section.id;
                return (
                  <div
                    className={classNames('pages-show--section', {open: isOpen})}
                    key={section.id}
                  >
                    <div
                      className="pages-show--section-header"
                      onClick={() => setOpenSectionId(isOpen ? null : section.id)}
                    >
                      <FontAwesomeIcon name={section.icon} />
                      <div className="pages-show--child-page-name">{section.name}</div>
                      <FontAwesomeIcon className="pages-show--section-chevron" name="chevron-right" />
                    </div>
                    {(isOpen && section.files.length > 0) && (
                      <div className="pages-show--section-files">
                        {section.files.map((file) => {
                          return (
                            <FilePreview
                              selected={selectedFileIds.has(file.id)}
                              file={file}
                              key={file.id}
                              onClick={openFile}
                            />
                          );
                        })}
                      </div>
                    )}
                    {(isOpen && section.files.length === 0) && (
                      <div className="pages-show--section-empty">
                        This section is empty
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {(page.files.length > 0 || page.childPages.length === 0) && (
            <>
              <div className="pages-show--header">Files</div>
              <Card className="pages-show--files">
                {page.files.map((file) => {
                  return (
                    <FilePreview
                      selected={selectedFileIds.has(file.id)}
                      file={file}
                      key={file.id}
                      onClick={openFile}
                    />
                  );
                })}
                {page.files.length === 0 && (
                  <NonIdealState
                    icon="document"
                    title="Nothing here"
                    description="Your administrator has yet to upload files to this section"
                  />
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default withMainLayout({viewerMenu: true})(PagesShowPage);
